import React, { Component, ErrorInfo, ReactNode } from "react";
import { AlertTriangle, RefreshCw, MessageSquare, Home } from "lucide-react";
import { Button } from "@/components/ui/button";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  showReportButton?: boolean;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  retryCount: number;
}

// 🚀 CHAT ERROR BOUNDARY
export class ChatErrorBoundary extends Component<Props, State> {
  private maxRetries = 3;

  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("🚨 Chat Error Boundary caught an error:", error, errorInfo);

    this.setState({
      error,
      errorInfo,
    });

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // 🚀 ERROR REPORTING: Send to monitoring service
    this.reportError(error, errorInfo);
  }

  // 🚀 ERROR REPORTING
  private reportError = (error: Error, errorInfo: ErrorInfo) => {
    try {
      // Example error reporting (replace with your service)
      // Send to error tracking service (Sentry, LogRocket, etc.)
      // Example: Sentry.captureException(error, {
      //   contexts: { react: errorInfo },
      //   extra: {
      //     message: error.message,
      //     stack: error.stack,
      //     componentStack: errorInfo.componentStack,
      //     timestamp: new Date().toISOString(),
      //     userAgent: navigator.userAgent,
      //     url: window.location.href,
      //     userId: this.getUserId(),
      //   }
      // });

      console.error("Error Report:", {
        error,
        errorInfo,
        userId: this.getUserId(),
      });
    } catch (reportingError) {
      console.error("Failed to report error:", reportingError);
    }
  };

  private getUserId = (): string | null => {
    try {
      const storedState = localStorage.getItem("auth-storage");
      if (storedState) {
        const parsedState = JSON.parse(storedState);
        return parsedState.state?.user?._id || null;
      }
      return null;
    } catch {
      return null;
    }
  };

  // 🚀 RETRY MECHANISM
  private handleRetry = () => {
    if (this.state.retryCount < this.maxRetries) {
      console.log(
        `🔄 Retrying chat (${this.state.retryCount + 1}/${this.maxRetries})`
      );

      this.setState({
        hasError: false,
        error: null,
        errorInfo: null,
        retryCount: this.state.retryCount + 1,
      });
    } else {
      console.warn("🚫 Max retry attempts reached");
    }
  };

  // 🚀 FORCE REFRESH
  private handleRefresh = () => {
    window.location.reload();
  };

  // 🚀 NAVIGATE TO HOME
  private handleGoHome = () => {
    window.location.href = "/";
  };

  // 🚀 COPY ERROR DETAILS
  private copyErrorDetails = () => {
    const errorDetails = {
      error: this.state.error?.message,
      stack: this.state.error?.stack,
      componentStack: this.state.errorInfo?.componentStack,
      timestamp: new Date().toISOString(),
    };

    navigator.clipboard
      .writeText(JSON.stringify(errorDetails, null, 2))
      .then(() => {
        // You could show a toast here
      })
      .catch((err) => console.error("Failed to copy error details:", err));
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className="flex flex-col items-center justify-center h-full min-h-[400px] p-6 text-center bg-background">
          <div className="max-w-md space-y-6">
            {/* Error Icon */}
            <div className="flex justify-center">
              <div className="p-3 bg-red-100 dark:bg-red-900/20 rounded-full">
                <AlertTriangle className="h-8 w-8 text-red-600 dark:text-red-400" />
              </div>
            </div>

            {/* Error Message */}
            <div className="space-y-2">
              <h2 className="text-xl font-semibold text-foreground">
                Sohbette Bir Hata Oluştu
              </h2>
              <p className="text-muted-foreground">
                Beklenmeyen bir hata oluştu. Lütfen sayfayı yenilemeyi veya
                tekrar denemeyi deneyin.
              </p>
            </div>

            {/* Error Details (Development) */}
            {process.env.NODE_ENV === "development" && this.state.error && (
              <div className="text-left bg-muted p-4 rounded-lg border text-sm font-mono">
                <div className="text-red-600 dark:text-red-400 font-semibold">
                  {this.state.error.message}
                </div>
                {this.state.error.stack && (
                  <div className="mt-2 text-muted-foreground text-xs overflow-auto max-h-32">
                    {this.state.error.stack}
                  </div>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              {/* Retry Button */}
              {this.state.retryCount < this.maxRetries && (
                <Button
                  onClick={this.handleRetry}
                  variant="default"
                  className="flex items-center gap-2"
                >
                  <RefreshCw className="h-4 w-4" />
                  Tekrar Dene ({this.maxRetries - this.state.retryCount} kalan)
                </Button>
              )}

              {/* Refresh Button */}
              <Button
                onClick={this.handleRefresh}
                variant="outline"
                className="flex items-center gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                Sayfayı Yenile
              </Button>

              {/* Home Button */}
              <Button
                onClick={this.handleGoHome}
                variant="ghost"
                className="flex items-center gap-2"
              >
                <Home className="h-4 w-4" />
                Ana Sayfa
              </Button>
            </div>

            {/* Report Button */}
            {this.props.showReportButton !== false && (
              <div className="pt-4 border-t border-border">
                <Button
                  onClick={this.copyErrorDetails}
                  variant="ghost"
                  size="sm"
                  className="text-muted-foreground hover:text-foreground"
                >
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Hata Detaylarını Kopyala
                </Button>
                <p className="text-xs text-muted-foreground mt-2">
                  Sorun devam ederse destek ekibiyle paylaşabilirsiniz
                </p>
              </div>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// 🚀 HOC (Higher Order Component) for easy wrapping
export const withChatErrorBoundary = <P extends object>(
  Component: React.ComponentType<P>,
  errorBoundaryProps?: Omit<Props, "children">
) => {
  const WrappedComponent = (props: P) => (
    <ChatErrorBoundary {...errorBoundaryProps}>
      <Component {...props} />
    </ChatErrorBoundary>
  );

  WrappedComponent.displayName = `withChatErrorBoundary(${
    Component.displayName || Component.name
  })`;

  return WrappedComponent;
};

// 🚀 SPECIFIC ERROR BOUNDARIES

export const MessageErrorBoundary: React.FC<{ children: ReactNode }> = ({
  children,
}) => (
  <ChatErrorBoundary
    onError={(error) => {
      console.error("🚨 Message rendering error:", error);
    }}
    fallback={
      <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
        <div className="flex items-center gap-2 text-red-700 dark:text-red-300">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          <span className="text-sm">Bu mesaj görüntülenemiyor</span>
        </div>
      </div>
    }
  >
    {children}
  </ChatErrorBoundary>
);

export const InputErrorBoundary: React.FC<{ children: ReactNode }> = ({
  children,
}) => (
  <ChatErrorBoundary
    onError={(error) => {
      console.error("🚨 Input component error:", error);
    }}
    fallback={
      <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
        <div className="text-center text-yellow-800 dark:text-yellow-200">
          <p className="text-sm">Mesaj giriş alanında sorun var</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-2 text-xs underline hover:no-underline"
          >
            Sayfayı yenile
          </button>
        </div>
      </div>
    }
  >
    {children}
  </ChatErrorBoundary>
);
